import { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { adminStyles } from "./adminStyles";

function useIsMobile(breakpoint = 760) {
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.innerWidth <= breakpoint;
    });

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= breakpoint);
        };

        handleResize();
        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, [breakpoint]);

    return isMobile;
}

export default function AdminDogsPage({
                                          primaryColor = "#8D6E63",
                                          secondaryColor = "#A1887F",
                                      }) {
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    const [dogs, setDogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");

    const responsiveStyles = useMemo(
        () => ({
            headerActions: {
                display: "flex",
                gap: "0.75rem",
                flexWrap: "wrap",
                width: isMobile ? "100%" : "auto",
            },

            headerButton: {
                flex: isMobile ? "1 1 100%" : "0 0 auto",
                textAlign: "center",
            },

            dogCard: {
                ...adminStyles.dashboardCard,
                display: "grid",
                gridTemplateColumns: isMobile
                    ? "1fr"
                    : "90px minmax(0, 1fr) auto",
                gap: isMobile ? "1.25rem" : "1rem",
                alignItems: isMobile ? "stretch" : "center",
                padding: isMobile ? "1rem" : adminStyles.dashboardCard.padding,
            },

            dogImage: {
                width: isMobile ? "100%" : "90px",
                height: isMobile ? "220px" : "90px",
                borderRadius: isMobile ? "20px" : "18px",
                overflow: "hidden",
                backgroundColor: "#f1ebe6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#777",
                fontSize: "0.85rem",
                textAlign: "center",
            },

            dogActions: {
                display: "grid",
                gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(3, max-content)",
                gap: "0.5rem",
                justifyContent: isMobile ? "stretch" : "flex-end",
                alignItems: "center",
            },

            actionButton: {
                width: isMobile ? "100%" : "auto",
                textAlign: "center",
                whiteSpace: "nowrap",
            },
        }),
        [isMobile]
    );

    useEffect(() => {
        loadDogs();
    }, []);

    async function loadDogs() {
        setIsLoading(true);
        setErrorMessage("");

        const { data, error } = await supabase
            .from("dogs")
            .select("*")
            .order("sort_order", { ascending: true })
            .order("created_at", { ascending: false });

        if (error) {
            console.error("Fehler beim Laden der Hunde:", error);
            setErrorMessage("Die Hunde konnten nicht geladen werden.");
            setIsLoading(false);
            return;
        }

        setDogs(data || []);
        setIsLoading(false);
    }

    async function handleToggleVisible(dog) {
        const nextVisibleState = !dog.is_visible;

        const { error } = await supabase
            .from("dogs")
            .update({
                is_visible: nextVisibleState,
                updated_at: new Date().toISOString(),
            })
            .eq("id", dog.id);

        if (error) {
            console.error("Fehler beim Aktualisieren:", error);
            alert("Der Hund konnte nicht aktualisiert werden.");
            return;
        }

        setDogs((currentDogs) =>
            currentDogs.map((item) =>
                item.id === dog.id
                    ? {
                        ...item,
                        is_visible: nextVisibleState,
                        updated_at: new Date().toISOString(),
                    }
                    : item
            )
        );
    }

    async function handleDelete(id) {
        const confirmed = window.confirm(
            "Möchtest du diesen Hund wirklich löschen?"
        );

        if (!confirmed) return;

        const { error } = await supabase.from("dogs").delete().eq("id", id);

        if (error) {
            console.error("Fehler beim Löschen:", error);
            alert("Der Hund konnte nicht gelöscht werden.");
            return;
        }

        setDogs((currentDogs) => currentDogs.filter((dog) => dog.id !== id));
    }

    return (
        <main style={adminStyles.dashboardPage}>
            <div style={adminStyles.dashboardContainer}>
                <header style={adminStyles.dashboardHeader}>
                    <div>
                        <h1 style={{ ...adminStyles.title, color: primaryColor }}>
                            Zuchthunde verwalten
                        </h1>
                        <p style={adminStyles.subtitle}>
                            Hündinnen und Rüden für die Webseite erstellen, bearbeiten und sortieren.
                        </p>
                    </div>

                    <div style={responsiveStyles.headerActions}>
                        <button
                            type="button"
                            onClick={() => navigate("/admin")}
                            style={{
                                ...adminStyles.secondaryButton,
                                ...responsiveStyles.headerButton,
                                backgroundColor: secondaryColor,
                            }}
                        >
                            Dashboard
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate("/admin/dogs/new")}
                            style={{
                                ...adminStyles.secondaryButton,
                                ...responsiveStyles.headerButton,
                                backgroundColor: primaryColor,
                            }}
                        >
                            Neuer Hund
                        </button>
                    </div>
                </header>

                {errorMessage && <div style={adminStyles.error}>{errorMessage}</div>}

                {isLoading ? (
                    <p style={{ color: "#666" }}>Hunde werden geladen...</p>
                ) : dogs.length === 0 ? (
                    <motion.div
                        style={adminStyles.dashboardCard}
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <h2 style={{ marginTop: 0, color: primaryColor }}>
                            Noch keine Hunde vorhanden
                        </h2>
                        <p style={{ color: "#666", lineHeight: 1.7 }}>
                            Lege den ersten Hund an, damit er in der DogsSection erscheinen kann.
                        </p>

                        <button
                            type="button"
                            onClick={() => navigate("/admin/dogs/new")}
                            style={{
                                ...adminStyles.secondaryButton,
                                backgroundColor: primaryColor,
                                marginTop: "1rem",
                                width: isMobile ? "100%" : "auto",
                            }}
                        >
                            Hund erstellen
                        </button>
                    </motion.div>
                ) : (
                    <div style={{ display: "grid", gap: "1rem" }}>
                        {dogs.map((dog, index) => (
                            <motion.article
                                key={dog.id}
                                style={responsiveStyles.dogCard}
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.35,
                                    delay: index * 0.04,
                                }}
                            >
                                <div style={responsiveStyles.dogImage}>
                                    {dog.image_src ? (
                                        <img
                                            src={dog.image_src}
                                            alt={dog.name}
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                objectFit: "cover",
                                            }}
                                        />
                                    ) : (
                                        "Kein Bild"
                                    )}
                                </div>

                                <div style={{ minWidth: 0 }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            gap: "0.5rem",
                                            flexWrap: "wrap",
                                            marginBottom: "0.65rem",
                                        }}
                                    >
                                        <span
                                            style={{
                                                display: "inline-block",
                                                padding: "0.3rem 0.75rem",
                                                borderRadius: "999px",
                                                backgroundColor: `${primaryColor}18`,
                                                color: primaryColor,
                                                fontSize: "0.85rem",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {dog.sex}
                                        </span>

                                        <span
                                            style={{
                                                display: "inline-block",
                                                padding: "0.3rem 0.75rem",
                                                borderRadius: "999px",
                                                backgroundColor: dog.is_visible
                                                    ? `${primaryColor}18`
                                                    : "#f1f1f1",
                                                color: dog.is_visible
                                                    ? primaryColor
                                                    : "#777",
                                                fontSize: "0.85rem",
                                                fontWeight: 700,
                                            }}
                                        >
                                            {dog.is_visible ? "Sichtbar" : "Ausgeblendet"}
                                        </span>
                                    </div>

                                    <h2
                                        style={{
                                            margin: "0 0 0.4rem",
                                            color: primaryColor,
                                            fontSize: "clamp(1.15rem, 4vw, 1.35rem)",
                                            lineHeight: 1.35,
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {dog.name}
                                    </h2>

                                    <p
                                        style={{
                                            margin: "0 0 0.5rem",
                                            color: "#666",
                                            lineHeight: 1.6,
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        {dog.subtitle || "Kein Untertitel vorhanden."}
                                    </p>

                                    <p
                                        style={{
                                            margin: 0,
                                            color: "#888",
                                            fontSize: "0.9rem",
                                        }}
                                    >
                                        Sortierung: {dog.sort_order ?? 0}
                                    </p>
                                </div>

                                <div style={responsiveStyles.dogActions}>
                                    <button
                                        type="button"
                                        onClick={() => handleToggleVisible(dog)}
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            ...responsiveStyles.actionButton,
                                            backgroundColor: dog.is_visible
                                                ? "#777"
                                                : primaryColor,
                                        }}
                                    >
                                        {dog.is_visible ? "Ausblenden" : "Einblenden"}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(`/admin/dogs/${dog.id}/edit`)
                                        }
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            ...responsiveStyles.actionButton,
                                            backgroundColor: secondaryColor,
                                        }}
                                    >
                                        Bearbeiten
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => handleDelete(dog.id)}
                                        style={{
                                            ...adminStyles.secondaryButton,
                                            ...responsiveStyles.actionButton,
                                            backgroundColor: "#b94a48",
                                        }}
                                    >
                                        Löschen
                                    </button>
                                </div>
                            </motion.article>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}